import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Home,
  LayoutDashboard,
  LogOut,
  Sparkles,
  Star,
  User,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_NAME, logoImage } from '../../config/brand';

const ease = 'duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === '/';
  const isOverlay = isHomePage && !isScrolled;
  const isRegisterPage = location.pathname === '/register';

  const isCurrentPath = (path: string, matchPrefix = false) =>
    matchPrefix ? location.pathname.startsWith(path) : location.pathname === path;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [isAuthenticated, currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (section && location.pathname === '/') {
        section.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (location.pathname !== '/') {
        window.location.href = `/#${sectionId}`;
        return;
      }
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    },
    [location.pathname, navigate],
  );

  const accentBar = isOverlay ? 'bg-[#C09048]' : 'bg-[#001848]';

  const navLinkClass = (active: boolean) =>
    `relative inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full px-3 text-sm font-semibold transition-[color,background-color,border-color,box-shadow,opacity] ${ease} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C09048]/50 focus-visible:ring-offset-2 ${
      active
        ? 'text-[#001848]'
        : 'text-[#5A6B82] hover:bg-[#EFE8DC]/80 hover:text-[#001848]'
    }`;

  const mobileLinkClass = (active: boolean) =>
    `flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-semibold transition-all ${
      active
        ? 'bg-[#001848] text-white shadow-md'
        : 'text-[#3A4A5C] hover:bg-[#EFE8DC] hover:text-[#001848]'
    }`;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50" dir="rtl">
        <div
          className={`transition-[background-color,border-color,box-shadow,backdrop-filter] ${ease} ${
            isOverlay
              ? 'border-b border-transparent bg-transparent backdrop-blur-[2px]'
              : 'border-b border-[#E5DCCF] bg-[#F5F1E8]/95 shadow-md shadow-[#001848]/5 backdrop-blur-xl'
          }`}
        >
          <div
            className={`mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 transition-[min-height,padding] ${ease} sm:px-6 lg:grid lg:grid-cols-[minmax(180px,1fr)_auto_minmax(180px,1fr)] lg:gap-4 lg:px-7 ${
              isOverlay ? 'min-h-[4.75rem]' : 'min-h-16 sm:min-h-[4.25rem]'
            }`}
          >
            {/* Logo */}
            <div className="order-2 justify-self-start lg:order-none">
              <Link
                to={mounted && isAuthenticated ? '/student-portal' : '/'}
                className="flex min-h-[44px] min-w-0 shrink-0 items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C09048]/50 focus-visible:ring-offset-2"
                aria-label={`${BRAND_NAME} — الصفحة الرئيسية`}
              >
                <span
                  className={`me-[0.55rem] inline-block h-[1.35rem] w-[0.2rem] rounded-full ${accentBar}`}
                  aria-hidden
                />
                <span
                  className={`inline-flex items-center justify-center rounded-2xl border px-2 py-1.5 transition-[background-color,border-color,box-shadow] ${ease} ${
                    isOverlay
                      ? 'border-white/50 bg-white/90 shadow-[0_4px_16px_rgba(0,24,72,0.12)]'
                      : 'border-[#E5DCCF] bg-white/90 shadow-[0_4px_16px_rgba(0,24,72,0.08)]'
                  }`}
                >
                  <img
                    src={logoImage}
                    alt={BRAND_NAME}
                    className="h-9 w-auto object-contain sm:h-10"
                    width={120}
                    height={40}
                    decoding="async"
                  />
                </span>
              </Link>
            </div>

            {/* Desktop nav */}
            <nav
              className="order-3 hidden items-center justify-self-center gap-1 lg:order-none lg:flex"
              aria-label="التنقل الرئيسي"
            >
              {mounted && isAuthenticated ? (
                <>
                  <Link
                    to="/student-portal"
                    className={navLinkClass(isCurrentPath('/student-portal', true))}
                    aria-current={isCurrentPath('/student-portal', true) ? 'page' : undefined}
                  >
                    بوابة الطالب
                    <span
                      className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full transition-opacity duration-500 ${accentBar} ${
                        isCurrentPath('/student-portal', true) ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-hidden
                    />
                  </Link>
                  {hasEnrolledCourses && (
                    <Link to="/student-portal" className={navLinkClass(false)}>
                      كورساتي وحصصي
                      <span
                        className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                        aria-hidden
                      />
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className={navLinkClass(isCurrentPath('/profile'))}
                    aria-current={isCurrentPath('/profile') ? 'page' : undefined}
                  >
                    حسابي
                    <span
                      className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full transition-opacity duration-500 ${accentBar} ${
                        isCurrentPath('/profile') ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-hidden
                    />
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    className={navLinkClass(isCurrentPath('/'))}
                    aria-current={isCurrentPath('/') ? 'page' : undefined}
                  >
                    الرئيسية
                    <span
                      className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full transition-opacity duration-500 ${accentBar} ${
                        isCurrentPath('/') ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-hidden
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={() => scrollToSection('teachers-section')}
                    className={navLinkClass(false)}
                  >
                    المدرسون
                    <span
                      className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection('why-choose-section')}
                    className={navLinkClass(false)}
                  >
                    لماذا نحن
                    <span
                      className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full opacity-0 ${accentBar}`}
                      aria-hidden
                    />
                  </button>
                </>
              )}
            </nav>

            {/* Actions */}
            <div className="order-4 hidden items-center justify-end justify-self-end gap-2 lg:order-none lg:flex">
              {mounted && isAuthenticated ? (
                <>
                  <div className="flex max-w-[150px] items-center gap-2 rounded-full border border-[#E5DCCF] bg-[#EFE8DC]/80 px-2 py-1">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#001848]">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <span className="truncate text-xs font-semibold text-[#001848] sm:text-sm">
                      {currentUser?.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={logout}
                    className={`inline-flex min-h-12 items-center justify-center gap-1.5 rounded-2xl border border-[#E5DCCF] px-[1.1rem] text-sm font-semibold text-[#001848] transition-[color,background-color,border-color,box-shadow] hover:border-red-200 hover:bg-red-50 hover:text-red-600 ${ease}`}
                  >
                    <LogOut className="h-4 w-4" />
                    خروج
                  </button>
                  <Link
                    to="/student-portal"
                    className={`inline-flex min-h-12 items-center justify-center gap-1.5 rounded-2xl border border-[#001848] bg-[#001848] px-[1.1rem] text-sm font-semibold text-white transition hover:bg-[#001230] ${ease}`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    البوابة
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-2xl border border-[#E5DCCF] bg-white/70 px-[1.1rem] text-sm font-semibold text-[#001848] transition-[color,background-color,border-color,box-shadow] hover:bg-[#EFE8DC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C09048]/50 focus-visible:ring-offset-2 ${ease}`}
                  >
                    تسجيل الدخول
                  </Link>
                  {!isRegisterPage && (
                    <Link
                      to="/register"
                      className={`inline-flex min-h-12 items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-[#001848] bg-[#001848] px-[1.1rem] text-sm font-semibold text-white transition hover:bg-[#001230] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C09048]/50 focus-visible:ring-offset-2 ${ease}`}
                    >
                      <Sparkles className="h-4 w-4" />
                      إنشاء حساب
                    </Link>
                  )}
                </>
              )}
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className={`order-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-[color,background-color,border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C09048]/50 lg:order-none lg:hidden ${ease} ${
                isOverlay
                  ? 'border-white/45 bg-white/80 text-[#001848] hover:bg-white'
                  : 'border-[#E5DCCF] bg-white/80 text-[#001848] hover:border-[#C09048] hover:bg-[#EFE8DC]'
              }`}
              aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <span aria-hidden className="flex flex-col gap-1.5">
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" dir="rtl">
          <button
            type="button"
            className="absolute inset-0 bg-[#001848]/25 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="إغلاق القائمة"
          />
          <div className="absolute inset-x-3 top-[4.75rem] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-[#E5DCCF] bg-[#FAF7F0] shadow-2xl shadow-[#001848]/10 sm:inset-x-4">
            <div className="border-b border-[#E5DCCF] px-4 py-3">
              <p className="text-xs font-semibold text-[#A87824]">القائمة</p>
              <p className="text-sm font-bold text-[#001848]">
                {mounted && isAuthenticated ? 'بوابة الطالب' : 'استكشف المنصة'}
              </p>
            </div>

            <div className="space-y-1 p-3">
              {mounted && isAuthenticated ? (
                <>
                  <Link
                    to="/student-portal"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={mobileLinkClass(isCurrentPath('/student-portal', true))}
                  >
                    <LayoutDashboard className="h-5 w-5 shrink-0" />
                    بوابة الطالب
                  </Link>
                  {hasEnrolledCourses && (
                    <Link
                      to="/student-portal"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={mobileLinkClass(false)}
                    >
                      <GraduationCap className="h-5 w-5 shrink-0" />
                      كورساتي وحصصي
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={mobileLinkClass(isCurrentPath('/profile'))}
                  >
                    <User className="h-5 w-5 shrink-0" />
                    حسابي
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={mobileLinkClass(isCurrentPath('/'))}
                  >
                    <Home className="h-5 w-5 shrink-0" />
                    الرئيسية
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      scrollToSection('teachers-section');
                    }}
                    className={mobileLinkClass(false)}
                  >
                    <Users className="h-5 w-5 shrink-0" />
                    المدرسون
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      scrollToSection('why-choose-section');
                    }}
                    className={mobileLinkClass(false)}
                  >
                    <Star className="h-5 w-5 shrink-0" />
                    لماذا نحن
                  </button>
                </>
              )}
            </div>

            <div className="space-y-2 border-t border-[#E5DCCF] p-3">
              {mounted && isAuthenticated ? (
                <button
                  type="button"
                  onClick={async () => {
                    setIsMobileMenuOpen(false);
                    await logout();
                    navigate('/', { replace: true });
                  }}
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                  تسجيل الخروج
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex min-h-11 items-center justify-center rounded-xl border border-[#E5DCCF] bg-white/70 px-4 py-3 text-sm font-semibold text-[#001848]"
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#001848] px-4 py-3 text-sm font-semibold text-white"
                  >
                    <Sparkles className="h-4 w-4" />
                    إنشاء حساب
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
